// src/pages/CustomerPortal/CustomerPortal.jsx

import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/formatters';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '32px',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh',
        background: '#f8fafc'
    },
    header: {
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
        marginTop: '8px'
    },
    dashboard: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '24px',
        marginBottom: '40px'
    },
    statCard: {
        background: 'white',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    statTitle: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        marginBottom: '8px',
        textTransform: 'uppercase'
    },
    statValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a'
    },
    section: {
        marginBottom: '40px'
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '20px'
    },
    box: {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#64748b',
        textTransform: 'uppercase'
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#334155'
    },
    statusBadge: {
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500'
    }
};

const getStatusStyle = (status) => {
    switch (status) {
        case 'Tamamlandı': return { background: '#f0fdf4', color: '#166534' };
        case 'Beklemede': return { background: '#fffbeb', color: '#92400e' };
        case 'İptal Edildi': return { background: '#fef2f2', color: '#991b1b' };
        default: return { background: '#f1f5f9', color: '#475569' };
    }
};

function CustomerPortal() {
    const { userData } = useAuth();
    const [customer, setCustomer] = useState(null);
    const [orders, setOrders] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData || userData.role !== 'customer' || !userData.companyId || !userData.customerId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;
        const customerId = userData.customerId;

        // Fetch Customer Info
        const unsubCustomer = onSnapshot(doc(db, 'companies', companyId, 'customers', customerId), (doc) => {
            if (doc.exists()) setCustomer(doc.data());
        });

        // Fetch Orders
        const ordersQuery = query(
            collection(db, 'companies', companyId, 'orders'),
            where('customerId', '==', customerId),
            orderBy('orderDate', 'desc')
        );
        const unsubOrders = onSnapshot(ordersQuery, (snapshot) => {
            setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Fetch Invoices
        const invoicesQuery = query(
            collection(db, 'companies', companyId, 'invoices'),
            where('customerId', '==', customerId),
            orderBy('invoiceDate', 'desc')
        );
        const unsubInvoices = onSnapshot(invoicesQuery, (snapshot) => {
            setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => {
            unsubCustomer();
            unsubOrders();
            unsubInvoices();
        };
    }, [userData]);

    if (loading) return <div style={styles.container}>Yükleniyor...</div>;
    if (!customer) return <div style={styles.container}>Hata: Müşteri bilgileri alınamadı.</div>;

    const balanceValue = customer.balance || 0;
    const balanceColor = balanceValue >= 0 ? '#10b981' : '#ef4444';

    return (
        <div style={styles.container}>
            <header style={styles.header}>
                <h1 style={styles.title}>Hoş Geldiniz, {customer.name}</h1>
                <p style={styles.subtitle}>Hesap özetinizi ve siparişlerinizi buradan takip edebilirsiniz.</p>
            </header>

            <div style={styles.dashboard}>
                <div style={styles.statCard}>
                    <div style={styles.statTitle}>Güncel Bakiye</div>
                    <div style={{ ...styles.statValue, color: balanceColor }}>
                        {balanceValue.toFixed(2)} {customer.currency === 'USD' ? '$' : '₺'}
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statTitle}>Açık Siparişler</div>
                    <div style={styles.statValue}>
                        {orders.filter(o => o.status === 'Beklemede').length}
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statTitle}>Toplam İşlem</div>
                    <div style={styles.statValue}>
                        {orders.length + invoices.length}
                    </div>
                </div>
            </div>

            <section style={styles.section}>
                <h2 style={styles.sectionTitle}>Son Siparişler</h2>
                <div style={styles.box}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Sipariş No</th>
                                <th style={styles.th}>Tutar</th>
                                <th style={styles.th}>Durum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.slice(0, 5).map(order => (
                                <tr key={order.id}>
                                    <td style={styles.td}>{new Date(order.orderDate?.seconds * 1000).toLocaleDateString('tr-TR')}</td>
                                    <td style={styles.td}>{order.id.slice(0, 8).toUpperCase()}</td>
                                    <td style={styles.td}>{order.totalAmount?.toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</td>
                                    <td style={styles.td}>
                                        <span style={{ ...styles.statusBadge, ...getStatusStyle(order.status) }}>
                                            {order.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {orders.length === 0 && (
                                <tr>
                                    <td colSpan="4" style={{ ...styles.td, textAlign: 'center' }}>Henüz sipariş bulunmuyor.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionTitle}>Son Faturalar</h2>
                <div style={styles.box}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Fatura No</th>
                                <th style={styles.th}>Toplam</th>
                                <th style={styles.th}>Vade</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.slice(0, 5).map(inv => (
                                <tr key={inv.id}>
                                    <td style={styles.td}>{new Date(inv.invoiceDate?.seconds * 1000).toLocaleDateString('tr-TR')}</td>
                                    <td style={styles.td}>{inv.invoiceNumber || inv.id.slice(0, 8).toUpperCase()}</td>
                                    <td style={styles.td}>{inv.total?.toFixed(2)} {inv.currency === 'USD' ? '$' : '₺'}</td>
                                    <td style={styles.td}>{inv.dueDate ? new Date(inv.dueDate?.seconds * 1000).toLocaleDateString('tr-TR') : '-'}</td>
                                </tr>
                            ))}
                            {invoices.length === 0 && (
                                <tr>
                                    <td colSpan="4" style={{ ...styles.td, textAlign: 'center' }}>Henüz fatura bulunmuyor.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

export default CustomerPortal;
